import { Modal, Skeleton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";

import { useListTemplates } from "../../../api/generated/endpoints/generation/generation";
import type { TemplateCard } from "../../../api/generated/model";
import { freshUrls } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { TemplateCover } from "../../../ui/TemplateCover";
import page from "../actors/actors.module.css";
import home from "../pro-home.module.css";
import picker from "./create-picker.module.css";
import { MakeTile, type MakeTileId, ToolTile } from "./make-tiles";
import classes from "./simple-sections.module.css";
import { freshStart } from "./start";
import { TEMPLATE_READ } from "./TemplateCards";
import { VIDEO_TOOLS } from "./video-tools";

/** The prototype's picker's tabs, in its order (`createPicker`): Ads first. */
const TABS = ["Ads", "Templates", "Video tools", "Images and actors", "Audio", "All"] as const;
type Tab = (typeof TABS)[number];

interface Group {
  label: Exclude<Tab, "Templates" | "All">;
  /** Its tiles, or the video tools' (`video-tools.ts`). */
  tiles: readonly MakeTileId[] | "tools";
}

/** The prototype's groups, each a tab and a heading on All. */
const GROUPS: readonly Group[] = [
  { label: "Ads", tiles: ["set", "ugc", "static", "animate", "winner", "storyboard"] },
  { label: "Video tools", tiles: "tools" },
  { label: "Images and actors", tiles: ["photos", "actor", "tweak"] },
  { label: "Audio", tiles: ["voiceover", "music", "sfx"] },
];

interface Ways {
  teamId: string;
  brandId: string;
  onPick: () => void;
}

function GroupTiles({ group, teamId, brandId, onPick }: Ways & { group: Group }) {
  const title = useId();
  return (
    <section className={picker.group} aria-labelledby={title}>
      <h3 id={title} className={picker.groupTitle}>
        {group.label}
      </h3>
      <div className={classes.tiles}>
        {group.tiles === "tools"
          ? VIDEO_TOOLS.map((tool) => (
              <ToolTile key={tool.name} tool={tool} teamId={teamId} brandId={brandId} onPick={onPick} />
            ))
          : group.tiles.map((id) => <MakeTile key={id} id={id} teamId={teamId} brandId={brandId} onPick={onPick} />)}
      </div>
    </section>
  );
}

/** A template as the sections' tile: its cover, tagged with what it makes, opening its form. */
function TemplateTile({ template, teamId, brandId, onPick }: Ways & { template: TemplateCard }) {
  return (
    <Link
      to="/teams/$teamId/brands/$brandId/simple/make"
      params={{ teamId, brandId }}
      search={{ kind: "ad", template: template.id }}
      state={freshStart}
      className={classes.tile}
      onClick={onPick}
    >
      <span className={classes.tileArt}>
        {/* Home's own covers: a clip's layers lie over its poster (PR 1764's review). */}
        <TemplateCover template={template} className={home.thumbCover} iconClassName={home.thumbIcon} />
        <span className={classes.tileKind}>{template.media === "video" ? "Video" : "Image"}</span>
      </span>
      <b className={classes.tileName}>{template.title}</b>
      <span className={classes.tileLine}>{template.description}</span>
    </Link>
  );
}

/**
 * The templates the catalog offers, the guided page's own read (`TEMPLATE_READ`), read only once
 * the tab opens and again before their covers' URLs expire; a template opens its form in the
 * guided page, which shows the list for one Simple cannot run (spec ui-perf/simple-home-rows).
 */
function Templates({ teamId, brandId, onPick }: Ways) {
  const templates = useListTemplates(teamId, TEMPLATE_READ, { query: freshUrls((read) => read.urls_expire_at) });
  if (templates.isError) return <ErrorAlert error={templates.error} />;
  const offered = templates.data?.items.filter((each) => each.available && !each.coming_soon);
  if (offered === undefined) {
    return (
      <div className={classes.tiles}>
        {[0, 1, 2].map((each) => (
          <Skeleton key={each} className={home.mediaSkeleton} />
        ))}
      </div>
    );
  }
  if (offered.length === 0) return <p className={classes.empty}>No templates yet.</p>;
  return (
    <div className={classes.tiles}>
      {offered.map((template) => (
        <TemplateTile key={template.id} template={template} teamId={teamId} brandId={brandId} onPick={onPick} />
      ))}
    </div>
  );
}

export interface CreatePickerProps extends Ways {
  opened: boolean;
  onClose: () => void;
}

/**
 * Create's picker (spec ui-perf/simple-create-picker): the Created AI Simple prototype's "What do
 * you want to make?", its tabs over every way to make something, each tile the sections' own. A
 * tile picked closes it (and the nav's drawer, `onPick`); a phone has it full screen.
 */
export function CreatePicker({ teamId, brandId, opened, onClose, onPick }: CreatePickerProps) {
  const [tab, setTab] = useState<Tab>("Ads");
  const phone = useMediaQuery("(max-width: 40em)") === true;
  const id = useId();
  const tabId = (index: number) => `${id}-tab-${index}`;
  const ways = { teamId, brandId, onPick };
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="What do you want to make?"
      size={1024}
      radius={24}
      padding={20}
      centered
      fullScreen={phone}
      classNames={{ title: picker.title }}
    >
      <div className={page.tabs} role="tablist" aria-label="What to make">
        {TABS.map((each, index) => (
          <button
            key={each}
            type="button"
            role="tab"
            id={tabId(index)}
            aria-selected={each === tab}
            aria-controls={`${id}-panel`}
            className={`${page.tab} ${picker.tab}`}
            onClick={() => setTab(each)}
          >
            {each}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={tabId(TABS.indexOf(tab))}>
        {tab === "Templates" ? (
          <Templates {...ways} />
        ) : (
          GROUPS.filter((group) => tab === "All" || group.label === tab).map((group) => (
            <GroupTiles key={group.label} group={group} {...ways} />
          ))
        )}
      </div>
    </Modal>
  );
}
